import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserNotConnectedError } from '@errors/UserNotConnectedError';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { loadServicesSuccess } from '@stores/services/services.action';
import { serviceSuggestionSetId } from '@stores/services/services.selector';
import {
  suggestionGetEligible,
  suggestionGetEligibleError,
  suggestionGetEligibleSuccess,
} from '@stores/suggestion/suggestion-ngrx.action';
import { userId } from '@wizbii-utils/angular/stores';
import { SuggestionsWebservice } from '@wizbii-utils/angular/webservices';
import { catchError, combineLatest, filter, map, of, switchMap } from 'rxjs';

export const initSuggestions = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(loadServicesSuccess),
      map(() => suggestionGetEligible())
    ),
  { functional: true }
);

export const suggestionGetEligibleEffect = createEffect(
  (
    actions$ = inject(Actions),
    suggestionsWebservice = inject(SuggestionsWebservice),
    store = inject(Store),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(suggestionGetEligible),
      switchMap(() =>
        combineLatest([
          store.select(userId).pipe(filter((userId) => !!userId)),
          store.select(serviceSuggestionSetId).pipe(filter((suggestionSetId) => !!suggestionSetId)),
        ])
      ),
      switchMap(([userId, suggestionSetId]) => {
        if (!userId) {
          throw new UserNotConnectedError();
        }

        if (!suggestionSetId) {
          throw new Error('No suggestion set id');
        }

        return suggestionsWebservice.getSuggestionsV2(suggestionSetId, userId, 'member').pipe(
          catchError((error) => {
            if (error.error?.status === 404 || error instanceof UserNotConnectedError) {
              router.navigate(['/', FeaturesRoutingEnum.HelpForm]);
            }
            return of(null);
          }),
          map((suggestionListing) => {
            if (suggestionListing) {
              return suggestionGetEligibleSuccess({
                suggestionListing: {
                  premiumSuggestions: suggestionListing.premium,
                  notPremiumSuggestions: suggestionListing.notPremium,
                  alreadyDepositSuggestions: suggestionListing.alreadyDeposit,
                  totalReward: suggestionListing.rewards,
                  suggestionIds: [...(suggestionListing.premium ?? []), ...(suggestionListing.notPremium ?? [])].map(
                    (suggestion) => suggestion.suggestionId
                  ),
                },
              });
            }
            return suggestionGetEligibleError();
          })
        );
      })
    ),
  { functional: true }
);
